import { Vector2, Vector3, Color4, Matrix } from '@babylonjs/core/Maths/math';
import { EasingFunction, CubicEase } from '@babylonjs/core/Animations/easing';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { Particle } from '@babylonjs/core/Particles/particle';

import { MeshSystem } from '../System/meshSystem';
import { Star, StarInterface } from './star';
import { MovingEntity } from '../Entity/movingEntity';
import { BLACKHOLE_DEPTH } from './blackHole';
import { PlanetMap } from '../Map/planetMap';

const BASE_COLOR = new Color4(0.3, 0.3, 0.3, 0);
const DIVIDE = new Vector3(2, 2, 2);

export class StarFighter extends Star {

    particleCurve: EasingFunction;
    target: StarFighter;
    absorber: MovingEntity;
    isDead = false;

    constructor(system: MeshSystem, planetMap: PlanetMap, starInterface: StarInterface) {
        super(system, planetMap, starInterface);

        this.particleCurve = new CubicEase();
        this.createParticle();
    }

    particle: ParticleSystem;
    createParticle() {
        this.particle = new ParticleSystem("particle", 100, this.system.scene);
        this.particle.emitRate = 100;
        this.particle.renderingGroupId = 3;

        this.particle.emitter = this.transformMesh.position;
        this.particle.minEmitBox = new Vector3(0, 0, 0); // Starting all from
        this.particle.maxEmitBox = new Vector3(0, 0, 0);
        this.particle.minLifeTime = 1;
        this.particle.maxLifeTime = 1;
        this.particle.minSize = 4;
        this.particle.maxSize = 4;

        this.particle.limitVelocityDamping = 0.9;

        // Start rotation
        this.particle.minInitialRotation = -Math.PI / 2;
        this.particle.maxInitialRotation = Math.PI / 2;
        this.particle.particleTexture = this.system.sparkleTexture;
        this.particle.blendMode = ParticleSystem.BLENDMODE_MULTIPLYADD;

        this.system.scene.registerBeforeRender(this.particle.animate);
    }

    setAbsobUpdateFunction() {
        // Use direction to initialize random value
        let that = this;
        this.particle.emitRate = 100;
        this.particle.startPositionFunction = (worldMatrix: Matrix, positionToUpdate: Vector3, particle: Particle, isLocal: boolean): void => {
            const { size, position, color } = that.target;
            const offset = size * 1;
            let x = Math.random() * offset;
            let y = Math.random() * offset;
            let z = Math.random() * offset;
            positionToUpdate.set(position.x + x, z, position.y + y);
            particle.color = color.subtract(BASE_COLOR);
            particle.startColor = particle.color.clone();
            const colorSub = that.color.subtract(BASE_COLOR);
            particle.changeColor = colorSub.subtract(particle.startColor);
        };
        
        // Must keep that because we need function word on particle
        this.particle.updateFunction = function(particles) {
            const endPosition = new Vector3(that.position.x + that.direction.x, 0, that.position.y + that.direction.y);
            const targetDist = Vector2.Distance(that.position, that.target.position);
            // const targetcolor = that.target.color;
            // const color = that.color;
            
            for (var index = 0; index < particles.length; index++) {
                var particle = particles[index];
                particle.age += 0.01;
                const targetcolor = particle.startColor;
                const changecolor = particle.changeColor;
                
                let dist = Vector3.Distance(particle.position, endPosition);
                if (dist < 2 || particle.age >= 1) { // Recycle
                    particles.splice(index, 1);
                    this._stockParticles.push(particle);
                    // particle.color.a = 0;
                    index--;
                    continue;
                } else {
                    const percDist = Math.max(Math.min(1 - dist/targetDist, 1), 0);
                    const perc = percDist + particle.age * 3;
                    const clampPerc = Math.max(perc, 0.2);
                    const progresscolor: Color4 = changecolor.multiply(new Color4(percDist, percDist, percDist, 1.0));
                    particle.color = progresscolor.add(targetcolor);
                    // const progressGradient = Math.min(particle.age, Math.pow(1 - particle.age, 1 / 2));
                    // particle.scale.x = progressGradient * 5;
                    // particle.scale.y = progressGradient * 5;
                    particle.scale.x = 1;
                    particle.scale.y = 1;

                    particle.position = Vector3.Lerp(particle.position, endPosition, clampPerc * 0.2);
                }
            } 
        }
        
        if (this.isStarVisible) this.particle.start();
    }

    setGetAbsobByBlackHoleFunction() {
        let that = this;
        this.particle.emitRate = 100;
        this.particle.startPositionFunction = (worldMatrix: Matrix, positionToUpdate: Vector3, particle: Particle, isLocal: boolean): void => {
            positionToUpdate.set(that.target.position.x, 0, that.target.position.y);
            particle.color = that.color.clone();
        };

        this.particle.updateFunction = function (particles) {
            const endPosition = new Vector3(that.position.x, BLACKHOLE_DEPTH / 4, that.position.y);
            const targetDist = Vector2.Distance(that.position, that.target.position);

            for (var index = 0; index < particles.length; index++) {
                var particle = particles[index];
                particle.age += 0.01;

                let dist = Vector3.Distance(particle.position, endPosition);
                if (dist < 2 || particle.age >= 1) { // Recycle
                    particles.splice(index, 1);
                    this._stockParticles.push(particle);
                    // particle.color.a = 0;
                    index--;
                    continue;
                } else {
                    const perc = 1 - dist/targetDist;
                    const clampPerc = Math.max(Math.min(perc, 1), 0.1) * (1 - particle.age);

                    const scale = clampPerc * 4;
                    particle.scale.x = scale;
                    particle.scale.y = scale;

                    particle.position = Vector3.Lerp(particle.position, endPosition, clampPerc * 0.2);
                }
            }
        }

        if (this.isStarVisible) this.particle.start();
    }

    absorbStopParticle() {
        this.particle.emitRate = 0;
    }

    explode(callback?: Function) {
        this._explode(callback);
    }
    _explode(callback?: Function) {
        const MAX_EXPLODE_DIST = 80;
        this.surfaceParticles.dispose();
        // this.coronaParticles.dispose();
        this.system.checkMaterials();
        // this.system.soundManager.playMesh('explodeStart', this.transformMesh);
        this.updateSize(10, 100, () => {
            this.setSize(0.01);
            let dist = Vector2.Distance(this.system.center, this.position);
            if (dist < MAX_EXPLODE_DIST) {
                let powerPerc = (MAX_EXPLODE_DIST - dist) / MAX_EXPLODE_DIST;
                let power = Math.pow(powerPerc, 1/2);
                this.system.cameraShake(power);
                this.system.soundManager.playMesh('explodeEnd', this.transformMesh, power);
            }
            setTimeout(() => {
                // Wait for the particle effect to end
                if (callback) callback();
            }, 4000);
            this.system.checkActiveMeshes();
        });
    }

    _disposeStarFighter() {
        this._disposeStar();
        setTimeout(() => {
            // this.particle.dispose();
        }, 2000);
        // this.particle.stop();
        this.system.scene.unregisterBeforeRender(this.particle.animate);
    }
}